/**
 * Validation of the inbound /authorize parameters, BEFORE any UI renders.
 *
 * GPGT-41350 states the rule plainly: "Validate app_code and redirect_uri BEFORE rendering any UI. A
 * foreign redirect_uri renders an error page on accounts.gozem.co and never redirects."
 *
 * So this module answers one question — may we show a sign-in form for this request? — and it
 * answers it before the router activates a page. The service validates independently and is the
 * authority; this is the client-side half, and its job is to make the failure visible on OUR origin
 * instead of bouncing the user somewhere we do not control.
 */

export type AuthorizeRejection =
  | 'invalid_request' // a required parameter is missing or malformed
  | 'invalid_redirect_uri'; // present, well-formed, and not ours to redirect to

export interface AuthorizeRequest {
  readonly appCode: string;
  readonly redirectUri: string;
  readonly state: string;
  readonly codeChallenge: string;
  readonly scope: string | null;
}

export type AuthorizeParseResult =
  | { readonly ok: true; readonly request: AuthorizeRequest }
  | { readonly ok: false; readonly reason: AuthorizeRejection };

/**
 * `app_code` is a stable identifier, not free text. Anything else is a malformed request.
 *
 * CASE-INSENSITIVE ON THE FIRST TWO CHARACTER CLASSES, and that is not cosmetic. Application codes
 * are registered in admin in UPPER CASE — the registered code for the merchant web app is
 * `PARTNERS`, and the specification's own examples say `DEVELOPERS` — while this pattern accepted
 * lower case only. The effect was that no value worked at all:
 *
 *   `PARTNERS`  -> refused here, before any UI rendered, as invalid_request
 *   `partners`  -> accepted here, then forwarded verbatim to a service that does not case-fold
 *                  either, which answered {"error":"unknown_application"}
 *
 * The value is still passed through UNCHANGED. This widens what is accepted; it does not normalise,
 * because the service compares the code verbatim and folding here would make a code work in this
 * app and fail in the service — the same trap `application-repository.js` calls out on its own side.
 */
const APP_CODE = /^[A-Za-z][A-Za-z0-9_]{1,63}$/;

/**
 * Whether a redirect_uri may be used.
 *
 * DELIBERATELY STRICT, and deliberately not a substring test. `startsWith` on a host is the classic
 * hole: `https://gozem.co.attacker.tld` starts with `https://gozem.co`. So the URL is parsed and the
 * HOSTNAME is compared exactly, or as a labelled subdomain.
 *
 * The allow-list is per-environment because staging redirects to staging hosts. Production ships the
 * production list; nothing here permits `http:` except an explicit localhost entry in development.
 */
export function isAllowedRedirectUri(
  candidate: string,
  allowedOrigins: readonly string[]
): boolean {
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return false;
  }
  // A fragment in a redirect_uri is not ours to preserve and OAuth forbids it.
  if (url.hash !== '') return false;
  return allowedOrigins.some((allowed) => {
    let allowedUrl: URL;
    try {
      allowedUrl = new URL(allowed);
    } catch {
      return false;
    }
    if (url.protocol !== allowedUrl.protocol) return false;
    if (url.port !== allowedUrl.port) return false;
    if (url.hostname === allowedUrl.hostname) return true;
    // A single leading dot in the allow-list entry opts that host into its subdomains.
    if (allowedUrl.hostname.startsWith('.')) {
      return url.hostname.endsWith(allowedUrl.hostname);
    }
    return false;
  });
}

/**
 * Parses and validates the query string of /authorize.
 *
 * Order matters: `redirect_uri` is checked for presence with everything else, but its ALLOWED-ness is
 * reported as its own reason, because the two produce different copy and only one of them means
 * "someone is trying something".
 */
export function parseAuthorizeRequest(
  params: URLSearchParams,
  allowedOrigins: readonly string[]
): AuthorizeParseResult {
  const appCode = params.get('app_code');
  const redirectUri = params.get('redirect_uri');
  const state = params.get('state');
  const codeChallenge = params.get('code_challenge');
  const challengeMethod = params.get('code_challenge_method');

  if (!appCode || !APP_CODE.test(appCode)) return { ok: false, reason: 'invalid_request' };
  if (!redirectUri) return { ok: false, reason: 'invalid_request' };
  if (!state) return { ok: false, reason: 'invalid_request' };
  if (!codeChallenge) return { ok: false, reason: 'invalid_request' };
  // S256 only. `plain` is a downgrade and there is no reason to accept it from a Gozem application.
  if (challengeMethod !== 'S256') return { ok: false, reason: 'invalid_request' };

  if (!isAllowedRedirectUri(redirectUri, allowedOrigins)) {
    return { ok: false, reason: 'invalid_redirect_uri' };
  }

  return {
    ok: true,
    request: { appCode, redirectUri, state, codeChallenge, scope: params.get('scope') },
  };
}
